/**
 * Brings a saved basket back in line with the live menu.
 *
 * Prices are copied into the basket when an item is added and the basket is kept
 * in localStorage forever, so a restaurant changing its menu leaves every open
 * basket holding prices that no longer exist. The backend recalculates from the
 * menu and refuses the order, which used to leave the customer stuck: the error
 * named a total they could not act on, and nothing in the app could refresh the
 * basket.
 *
 * Give this the basket rows and the menu categories from /api/store and it
 * returns corrected rows, a list of what moved, and anything no longer on the
 * menu. Nothing is hidden from the customer: the caller shows the changes and
 * lets them decide.
 */

/** Price of one basket row, matching the rule used on the cart and checkout pages. */
export function rowTotal(item) {
  const basePrice = item.special_price == 0 ? item.price : item.special_price;
  const itemTotal = basePrice * item.quantity;

  const optionsTotal = (item.option_groups || []).reduce((sum, group) => {
    return (group.options || []).reduce((groupSum, option) => {
      if (option.isSelected === true) {
        return groupSum + option.price * (option.quantity || 1);
      }
      return groupSum;
    }, sum);
  }, 0);

  return Number((itemTotal + optionsTotal).toFixed(2));
}

export function cartTotal(cartData) {
  return Number(
    (cartData || []).reduce((total, item) => total + rowTotal(item), 0).toFixed(2)
  );
}

/**
 * Flattens a menu tree into { itemId: item } for lookup.
 *
 * Accepts either the categories of one menu or the whole menus array, because a
 * restaurant can have more than one menu and a basket row from the second one
 * must not look "missing" and get thrown away.
 */
function indexMenu(source) {
  const byId = {};

  const walk = (list) => {
    (list || []).forEach((entry) => {
      if (entry && Array.isArray(entry.categories)) {
        walk(entry.categories); // a menu rather than a category
        return;
      }

      (entry?.items || []).forEach((item) => {
        byId[item.id] = item;
      });
      walk(entry?.sub_categories);
    });
  };

  walk(source);

  return byId;
}

/**
 * @param {Array}  cartData   basket rows as stored in redux
 * @param {Array}  categories categories from /api/store (menus[0].categories)
 * @returns {{cartData: Array, changes: Array, removed: Array, totalAmount: number}}
 */
export function reconcileCart(cartData, categories) {
  const menu = indexMenu(categories);
  const changes = [];
  const removed = [];
  const nextCart = [];

  (cartData || []).forEach((item) => {
    const menuItem = menu[item.id];

    // Withdrawn by the restaurant: gone from the payload, or still in it but
    // switched off. Being out of stock is different and deliberately left alone
    // here - that row stays, flagged, because it is usually back tomorrow and
    // the backend blocks checkout while it lasts.
    if (!menuItem || Number(menuItem.status) === 0) {
      removed.push({ name: item.name, reason: "no longer on the menu" });
      return;
    }

    const next = { ...item };

    if (Number(menuItem.price) !== Number(item.price)) {
      changes.push({
        name: item.name,
        from: Number(item.price),
        to: Number(menuItem.price),
      });
      next.price = menuItem.price;
    }

    if (Number(menuItem.special_price ?? 0) !== Number(item.special_price ?? 0)) {
      changes.push({
        name: item.name,
        from: Number(item.special_price ?? 0),
        to: Number(menuItem.special_price ?? 0),
      });
      next.special_price = menuItem.special_price;
    }

    next.is_in_stock = menuItem.is_in_stock;

    // Options are matched by id, so a renamed or reordered group is harmless.
    const menuOptions = {};
    (menuItem.option_groups || []).forEach((group) => {
      (group.options || []).forEach((option) => {
        menuOptions[option.id] = option;
      });
    });

    let optionMissing = null;

    next.option_groups = (item.option_groups || []).map((group) => ({
      ...group,
      options: (group.options || []).map((option) => {
        const menuOption = menuOptions[option.id];

        if (!menuOption) {
          // Only a chosen option matters; an unselected one can just go.
          if (option.isSelected) {
            optionMissing = option.name;
          }
          return option;
        }

        if (Number(menuOption.price) !== Number(option.price)) {
          if (option.isSelected) {
            changes.push({
              name: `${item.name} - ${option.name}`,
              from: Number(option.price),
              to: Number(menuOption.price),
            });
          }
          return { ...option, price: menuOption.price };
        }

        return option;
      }),
    }));

    if (optionMissing) {
      removed.push({
        name: item.name,
        reason: `"${optionMissing}" is no longer available`,
      });
      return;
    }

    next.total = rowTotal(next);
    nextCart.push(next);
  });

  return {
    cartData: nextCart,
    changes,
    removed,
    totalAmount: cartTotal(nextCart),
  };
}

/**
 * One line for the customer, or null when the basket was already correct.
 *
 * Kept gentle on purpose: the basket has already been put right, so this is a
 * notice rather than a failure, and the customer can carry straight on.
 */
export const PRICES_UPDATED_NOTICE =
  'Menu prices have been updated. Please continue with the order.';

export function describeCartChanges({ changes, removed }) {
  if (!changes.length && !removed.length) {
    return null;
  }

  if (!removed.length) {
    return PRICES_UPDATED_NOTICE;
  }

  const gone =
    removed.length === 1
      ? `${removed[0].name} is no longer available and has been removed from your order.`
      : `${removed.length} items are no longer available and have been removed from your order.`;

  return changes.length ? `${gone} ${PRICES_UPDATED_NOTICE}` : gone;
}

export default reconcileCart;
